import { withNamespaces } from 'react-i18next';
import {
    Badge,
    Card,
    CardBody,
    CardTitle,
    Col,
    Row,
} from 'reactstrap';

import GeoMap from 'src/modules/3rd-party-management/components/geo-map';
import { GeocledianDeforestationRisks } from 'src/modules/3rd-party-management/constants';
import { STANDARD_DATE_FORMAT } from 'src/components/constants';
import DateUtils from 'src/services/utils/DateUtils';

import useGeolocationData from './hooks/useGeolocationData';

const dateUtils = new DateUtils();

const GeolocationDataSkeleton = () => (
    <Card className="h-100 mb-0">
        <CardBody>
            <div className="dt-field dt-skeleton mb-4" style={{ width: '25%', height: '24px' }} />
            <Row className="g-4">
                <Col xs="12" lg="8">
                    {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                        <div
                            key={item}
                            className="dt-field dt-skeleton mb-3"
                            style={{ width: '100%', height: '36px' }}
                        />
                    ))}
                </Col>
                <Col xs="12" lg="4">
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }} />
                </Col>
            </Row>
        </CardBody>
    </Card>
);

const DetailRow = ({
    label,
    value,
}) => (
    <div className="d-flex justify-content-between align-items-center gap-3 py-2 mb-3">
        <span className="text-muted">
            {label}
        </span>
        <span className="fw-semibold text-end">
            {value || '—'}
        </span>
    </div>
);

export const GeolocationDataCard = ({
    t,
    data,
    isLoading = false,
    isFetching = false,
    isError = false,
}) => {
    if (isLoading || isFetching) {
        return <GeolocationDataSkeleton />;
    }

    if (isError || !data) {
        return null;
    }

    const riskConfig = GeocledianDeforestationRisks[data.deforestationRisk?.toLowerCase()];

    const primaryDetails = [
        {
            key: 'supplier',
            label: t('Supplier'),
            value: data.supplier,
        },
        {
            key: 'country',
            label: t('Country'),
            value: data.country,
        },
        {
            key: 'material',
            label: t('Material'),
            value: data.material,
        },
    ];

    const secondaryDetails = [
        {
            key: 'forestCoverage',
            label: t('Forest Coverage'),
            value: data.forestCoverage,
        },
        {
            key: 'deforestationRisk',
            label: t('Deforestation Risk'),
            value: riskConfig ? (
                <Badge style={{ backgroundColor: riskConfig.color }}>
                    {t(riskConfig.title)}
                </Badge>
            ) : data.deforestationRisk,
        },
        {
            key: 'gpsValidation',
            label: t('GPS Validation'),
            value: (
                <Badge color="warning" className="text-dark">
                    {t(data.gpsValidation)}
                </Badge>
            ),
        },
        {
            key: 'lastSatelliteCheck',
            label: t('Last Satellite Check'),
            value: data.lastSatelliteCheck
                ? dateUtils.convertDateToDate(data.lastSatelliteCheck, STANDARD_DATE_FORMAT)
                : null,
        },
    ];

    return (
        <Card className="eudr-geolocation-data h-100 mb-0">
            <CardBody>
                <CardTitle tag="h6" className="overview-section-title mb-4">
                    {t('Geolocation Data')}
                </CardTitle>

                <Row className="g-4 align-items-stretch">
                    <Col xs="12" lg="7">
                        <div className="h-100 me-4">
                            <Row className="g-3 mb-4">
                                {primaryDetails.map((detail) => (
                                    <Col key={detail.key} xs="12" md="4">
                                        <span className="d-block text-muted mb-1">
                                            {detail.label}
                                        </span>
                                        <span className="d-block fw-semibold">
                                            {detail.value || '—'}
                                        </span>
                                    </Col>
                                ))}
                            </Row>

                            {secondaryDetails.map((detail) => (
                                <DetailRow
                                    key={detail.key}
                                    label={detail.label}
                                    value={detail.value}
                                />
                            ))}
                        </div>
                    </Col>

                    <Col xs="12" lg="5">
                        <div className="border rounded overflow-hidden">
                            <GeoMap
                                readOnly={true}
                                preDefinedGeo={data.geolocation}
                                multiDraw={true}
                                defaultSearchParam={data.country}
                                displayZoomToShapesBtn={false}
                                styles={{
                                    height: '320px',
                                }}
                            />
                        </div>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

const GeolocationData = ({
    supplierId,
    t,
}) => {
    const query = useGeolocationData({
        supplierId,
        t,
    });

    return (
        <GeolocationDataCard
            t={t}
            data={query.data}
            isLoading={query.isLoading}
            isFetching={query.isFetching}
            isError={query.isError}
        />
    );
};

export default withNamespaces()(GeolocationData);
